import { z } from 'zod';
import { Decimal } from 'decimal.js';
import { amountScale } from './currency.js';
/**
 * Money crosses the API as a decimal string, never a JS number.
 * Matches PostgreSQL NUMERIC(20,4): up to 16 integer digits and up to 4 decimals.
 */
export const moneyAmountSchema = z
    .string()
    .regex(/^-?(0|[1-9]\d{0,15})(\.\d{1,4})?$/, 'Amount must be a decimal string like "125.5000"');
/** Exchange rate as a decimal string, matching NUMERIC(24,10), always positive. */
export const exchangeRateSchema = z
    .string()
    .regex(/^(0|[1-9]\d{0,13})(\.\d{1,10})?$/, 'Rate must be a decimal string like "89500.0000000000"')
    .refine((value) => /[1-9]/.test(value), 'Rate must be positive');
/**
 * Decimal arithmetic for money (CLAUDE.md §3.1). Plenty of precision for NUMERIC(20,4) sums;
 * results are always returned as decimal strings with exactly 4 decimals, like PostgreSQL does.
 */
const MoneyDecimal = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_EVEN });
export const MONEY_SCALE = 4;
function money(value) {
    return new MoneyDecimal(moneyAmountSchema.parse(value));
}
/** "12.5" -> "12.5000". Throws if the value is not a valid money string. */
export function formatMoney(value) {
    return money(value).toFixed(MONEY_SCALE);
}
export function addMoney(...values) {
    return values.reduce((sum, v) => sum.plus(money(v)), new MoneyDecimal(0)).toFixed(MONEY_SCALE);
}
export function subtractMoney(a, b) {
    return money(a).minus(money(b)).toFixed(MONEY_SCALE);
}
export function negateMoney(value) {
    return money(value).negated().toFixed(MONEY_SCALE);
}
export function isZeroMoney(value) {
    return money(value).isZero();
}
export function isNegativeMoney(value) {
    return money(value).lt(0);
}
/** -1, 0 or 1. */
export function compareMoney(a, b) {
    return money(a).comparedTo(money(b));
}
/**
 * Turns what a person typed into a money string, or null if it is not a valid amount.
 * Accepts a comma as the decimal separator ("12,5") and ignores spaces.
 */
export function parseMoneyInput(raw) {
    const cleaned = raw.replace(/\s/g, '').replace(',', '.');
    const normalized = cleaned.startsWith('.') ? `0${cleaned}` : cleaned;
    const result = moneyAmountSchema.safeParse(normalized);
    return result.success ? result.data : null;
}
// ---------------------------------------------------------------------------------------------
// Multi-currency (Phase 3). CLAUDE.md §3.2: a rate is "units of the currency per 1 unit of the
// family's base currency" (1 USD = 89,500 LBP is stored as 89500, never as 1/89500), and
//   base_amount = round(amount / rate_to_base, 4), half away from zero.
// PostgreSQL's round(numeric) rounds the same way, and the database re-checks every posting.
// ---------------------------------------------------------------------------------------------
/** Exact division, then rounding to 4 decimals with halves going away from zero. */
const BaseDecimal = Decimal.clone({ precision: 60, rounding: Decimal.ROUND_HALF_UP });
function rate(value) {
    return new BaseDecimal(exchangeRateSchema.parse(value));
}
/** True when the amount has no more decimals than the currency allows (LBP: whole numbers). */
export function fitsCurrencyScale(amount, scale) {
    const value = money(amount);
    return value.equals(value.toDecimalPlaces(scale, Decimal.ROUND_DOWN));
}
/** The base-currency value of one amount: round(amount / rate, 4), half away from zero. */
export function toBaseAmount(amount, rateToBase) {
    return new BaseDecimal(moneyAmountSchema.parse(amount))
        .dividedBy(rate(rateToBase))
        .toDecimalPlaces(MONEY_SCALE, Decimal.ROUND_HALF_UP)
        .toFixed(MONEY_SCALE);
}
/**
 * Base amounts for the lines of ONE currency in one transaction (they sum to zero in that
 * currency and share one rate). Each line is converted with toBaseAmount; if rounding leaves the
 * base amounts a little off zero, the whole remainder goes to the line with the largest amount
 * (the first such line on a tie), so the transaction also balances exactly in base currency.
 */
export function allocateBaseAmounts(amounts, rateToBase) {
    const base = amounts.map((amount) => new MoneyDecimal(toBaseAmount(amount, rateToBase)));
    const remainder = base.reduce((sum, v) => sum.plus(v), new MoneyDecimal(0));
    if (!remainder.isZero() && amounts.length > 0) {
        let largest = 0;
        amounts.forEach((amount, index) => {
            if (money(amount).abs().gt(money(amounts[largest]).abs()))
                largest = index;
        });
        base[largest] = base[largest].minus(remainder);
    }
    return base.map((v) => v.toFixed(MONEY_SCALE));
}
/** "89500" -> "89500.0000000000", like PostgreSQL NUMERIC(24,10) returns it. */
export function formatRate(value) {
    return rate(value).toFixed(10);
}
/** "89500.0000000000" -> "89500" for display and comparison (no trailing zeros). */
export function trimRate(value) {
    return rate(value).toFixed();
}
/** -1, 0 or 1. */
export function compareRates(a, b) {
    return rate(a).comparedTo(rate(b));
}
/**
 * Turns a typed rate into a rate string, or null. Commas and spaces are thousand separators
 * ("89,500" and "89 500" are 89500); a dot is the decimal point.
 */
export function parseRateInput(raw) {
    const cleaned = raw.replace(/[\s,]/g, '');
    const normalized = cleaned.startsWith('.') ? `0${cleaned}` : cleaned;
    const result = exchangeRateSchema.safeParse(normalized);
    return result.success ? result.data : null;
}
const SUFFIXES = { k: '1000', m: '1000000' };
/**
 * Turns a typed amount in a currency into a money string, or null when it is not a valid amount
 * for that currency. Built for fast Lebanese pound entry:
 * - "500k" is 500,000 and "1.5m" is 1,500,000 (any currency);
 * - spaces are ignored; commas are thousand separators ("1,500,000", "89,500" LBP), except a
 *   single comma with no dot where decimals make sense: "12,5" USD is 12.5, "2,5m" is 2.5m;
 * - the result must fit the currency (LBP: whole numbers only, so "1.5" LBP is null).
 */
export function parseAmountInput(raw, currency) {
    const scale = amountScale(currency);
    let text = raw.replace(/\s/g, '').toLowerCase();
    const multiplier = SUFFIXES[text.slice(-1)];
    if (multiplier)
        text = text.slice(0, -1);
    const commas = (text.match(/,/g) ?? []).length;
    const thousands = text.includes('.') || commas > 1 || (commas === 1 && scale === 0 && multiplier === undefined);
    text = thousands ? text.replace(/,/g, '') : text.replace(',', '.');
    const match = /^(-?)(\d*)(\.\d*)?$/.exec(text);
    if (!match || (match[2] === '' && (match[3] ?? '.').length < 2))
        return null;
    const value = new MoneyDecimal(`${match[1]}${match[2] || '0'}${match[3] ?? ''}`.replace(/\.$/, '')).times(multiplier ?? '1');
    if (!value.equals(value.toDecimalPlaces(scale, Decimal.ROUND_DOWN)))
        return null;
    const result = moneyAmountSchema.safeParse(value.toFixed());
    return result.success ? result.data : null;
}
/**
 * `part` as a whole percent of `whole` ("37"), rounded half up, capped at 0..`cap`; null when
 * `whole` is zero. For progress bars ("37% of income spent"); exact decimal math, no floats.
 */
export function percentOf(part, whole, cap = 100) {
    const total = money(whole);
    if (total.isZero())
        return null;
    const value = money(part).dividedBy(total).times(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
    return Decimal.max(0, Decimal.min(cap, value)).toFixed(0);
}
/**
 * Converts an amount between two currencies through the base currency, using each one's rate
 * (1 base = rate; the base currency itself is "1"): amount / fromRate × toRate, rounded half
 * away from zero to `scale` decimals.
 */
export function convertAmount(amount, fromRate, toRate, scale) {
    return new BaseDecimal(moneyAmountSchema.parse(amount))
        .dividedBy(rate(fromRate))
        .times(rate(toRate))
        .toDecimalPlaces(scale, Decimal.ROUND_HALF_UP)
        .toFixed(MONEY_SCALE);
}
/**
 * The rate a deal implies, "1 base = X quote": quoteAmount / baseAmount, rounded half away from
 * zero to the 10 decimals a rate keeps. Both amounts are taken as positive.
 */
export function dealRate(quoteAmount, baseAmount) {
    return new BaseDecimal(moneyAmountSchema.parse(quoteAmount))
        .abs()
        .dividedBy(new BaseDecimal(moneyAmountSchema.parse(baseAmount)).abs())
        .toDecimalPlaces(10, Decimal.ROUND_HALF_UP)
        .toFixed(10);
}
/**
 * How many decimals people use for a currency's everyday amounts: at most 2 (cents), and none
 * for whole-number currencies such as LBP. Used for amounts the app works out (estimates).
 */
export function displayScale(currency) {
    return Math.min(amountScale(currency), 2);
}
//# sourceMappingURL=money.js.map